import type { ApiErrorCodeI18nPatternRule } from "./error-code-i18n-pattern-types";

// Environment-center error-code i18n pattern rules.
export const API_ERROR_CODE_I18N_PLATFORM_ENVIRONMENT_PATTERN_RULES: readonly ApiErrorCodeI18nPatternRule[] =
  [
    {
      prefix: "platform.environment.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironment",
      reasonCodes: [
        "actor_id_invalid",
        "integration_not_configured",
        "internal_token_missing",
        "invalid_payload",
        "positive_integer_required",
        "request_failed",
        "resource_not_found",
        "unsupported_kind",
        "upstream_rejected",
      ],
    },
    {
      prefix: "platform.environment.asset_reference.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentAssetReference",
      reasonCodes: [
        "actor_user_required",
        "asset_id_required",
        "base_url_missing",
        "download_failed",
        "public_id_missing",
        "public_id_required",
        "request_failed",
        "response_invalid",
        "upstream_rejected",
      ],
    },
    {
      prefix: "platform.environment.auth.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentAuth",
      reasonCodes: [
        "environment_admin_required",
        "identity_user_id_missing",
        "internal_actor_user_id_required",
        "invalid_app_session",
        "invalid_internal_token",
      ],
    },
    {
      prefix: "platform.environment.business_code.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentBusinessCode",
      reasonCodes: ["allocation_invalid", "sequence_exhausted"],
    },
    {
      prefix: "platform.environment.business_event.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentBusinessEvent",
      reasonCodes: [
        "body_invalid",
        "consumer_loop_failed",
        "contract_invalid",
        "envelope_invalid",
        "event_type_unexpected",
        "outbox_claim_failed",
        "payload_invalid",
        "publish_disabled",
        "publish_failed",
        "retryable_processing_failed",
        "source_service_unexpected",
      ],
    },
    {
      prefix: "platform.environment.business_job.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentBusinessJob",
      reasonCodes: ["enqueue_failed", "queue_unavailable", "stream_key_missing"],
    },
    {
      prefix: "platform.environment.dependency.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentDependency",
      reasonCodes: [
        "asset_center_internal_token_missing",
        "business_reference_internal_token_missing",
        "execution_fabric_base_url_missing",
        "execution_fabric_request_failed",
        "execution_fabric_response_invalid",
        "execution_fabric_upstream_rejected",
        "identity_lookup_failed",
        "identity_payload_invalid",
        "sso_base_url_missing",
      ],
    },
    {
      prefix: "platform.environment.image.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentImage",
      reasonCodes: [
        "field_invalid",
        "metadata_reserved_key_forbidden",
        "name_required",
        "positive_integer_required",
      ],
    },
    {
      prefix: "platform.environment.image_ports.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentImagePorts",
      reasonCodes: ["invalid"],
    },
    {
      prefix: "platform.environment.physical_device.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentPhysicalDevice",
      reasonCodes: ["archived_read_only", "batch.ids_required", "not_found"],
    },
    {
      prefix: "platform.environment.runtime_node.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentRuntimeNode",
      reasonCodes: [
        "inventory_request_failed",
        "inventory_response_invalid",
        "inventory_upstream_rejected",
        "not_found",
        "runtime_base_url_missing",
      ],
    },
    {
      prefix: "platform.environment.spec.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentSpec",
      reasonCodes: [
        "archived_read_only",
        "batch.ids_required",
        "not_found",
        "runtime_type_invalid",
        "vm_disk_binding_forbidden",
      ],
    },
    {
      prefix: "platform.environment.topology.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentTopology",
      reasonCodes: [
        "traffic_dependency_rejected",
        "traffic_dependency_request_failed",
        "traffic_dependency_response_invalid",
        "traffic_library_config_missing",
        "traffic_picker_request_invalid",
      ],
    },
    {
      prefix: "platform.environment.topology_default_image_policy.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentDefaultImagePolicy",
      reasonCodes: [
        "frr_router_implementation_required",
        "network_category_required",
        "system_core_role_required",
      ],
    },
    {
      prefix: "platform.environment.topology_template.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentTopologyTemplate",
      reasonCodes: [
        "name_conflict",
        "not_found",
        "owner_forbidden",
        "share_access_forbidden",
        "share_mode_invalid",
      ],
    },
    {
      prefix: "platform.environment.topology_version.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentTopologyVersion",
      reasonCodes: [
        "name_conflict",
        "not_found",
        "owner_forbidden",
        "published_status_transition_forbidden",
        "share_access_forbidden",
        "share_mode_invalid",
        "source_not_found",
      ],
    },
    {
      prefix: "platform.environment.validation.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentValidation",
      reasonCodes: [
        "application_image_source_type_invalid",
        "container_image_category_invalid",
        "container_image_source_type_invalid",
        "custom_image_source_type_invalid",
        "image_usage_type_invalid",
        "status_invalid",
      ],
    },
    {
      prefix: "platform.environment.application_image.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentApplicationImage",
      reasonCodes: [
        "active_source_required",
        "archived_read_only",
        "cloud_init_profile_forbidden_when_disabled",
        "cloud_init_profile_guest_profile_unsupported",
        "cloud_init_profile_invalid",
        "cloud_init_profile_os_family_unsupported",
        "cloud_init_profile_required",
        "duplicate_name_allocation_failed",
        "name_conflict",
        "not_found",
        "owner_forbidden",
        "share_access_forbidden",
        "share_mode_invalid",
      ],
    },
    {
      prefix: "platform.environment.base_image.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentBaseImage",
      reasonCodes: [
        "activation_archived_forbidden",
        "activation_asset_source_required",
        "already_active",
        "archived_asset_conflict",
        "archived_read_only",
        "archived_status_transition_forbidden",
        "asset_required",
        "cloud_init_redetect_asset_source_required",
        "duplicate_name_allocation_failed",
        "name_conflict",
        "node_local_ref_invalid",
        "not_found",
        "owner_forbidden",
        "prewarm_active_required",
        "prewarm_asset_source_required",
        "prewarm_source_unpullable",
        "reference_type_invalid",
        "runtime_commit_login_credentials_forbidden",
        "share_access_forbidden",
        "share_mode_invalid",
        "share_target_invalid",
        "source_ref_required",
        "source_type_asset_required",
        "virtualization_type_invalid",
      ],
    },
    {
      prefix: "platform.environment.custom_image.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentCustomImage",
      reasonCodes: [
        "active_source_required",
        "archived_read_only",
        "duplicate_name_allocation_failed",
        "name_conflict",
        "not_found",
        "owner_forbidden",
        "share_access_forbidden",
        "share_mode_invalid",
      ],
    },
    {
      prefix: "platform.environment.container_image.",
      resourceI18nKey: "common.errors.apiCodeResources.platformEnvironmentContainerImage",
      reasonCodes: [
        "actor_user_required",
        "archived_asset_conflict",
        "archived_read_only",
        "archived_reimport_forbidden",
        "asset_conflict",
        "asset_type_invalid",
        "batch.ids_required",
        "batch.target_fields_required",
        "created_by_mismatch",
        "docker_command_timed_out",
        "docker_load_failed",
        "docker_loaded_image_missing",
        "docker_login_failed",
        "docker_push_failed",
        "docker_runtime_unavailable",
        "docker_tag_failed",
        "duplicate_name_allocation_failed",
        "harbor_api_base_url_invalid",
        "harbor_api_base_url_missing",
        "harbor_operator_credentials_missing",
        "harbor_operator_host_misconfigured",
        "harbor_project_create_failed",
        "harbor_project_query_failed",
        "harbor_publish_empty",
        "harbor_request_failed",
        "harbor_robot_create_failed",
        "harbor_robot_credentials_missing",
        "harbor_robot_delete_failed",
        "harbor_robot_query_failed",
        "harbor_robot_response_invalid",
        "import_task_not_found",
        "import_task_status_invalid",
        "name_conflict",
        "not_found",
        "owner_forbidden",
        "owner_missing",
        "registry_host_mismatch",
        "registry_host_required",
        "registry_image_tag_required",
        "registry_owner_project_name_invalid",
        "registry_owner_required",
        "registry_owner_user_required",
        "registry_project_prefix_invalid",
        "registry_ref_required",
        "registry_repository_path_required",
        "registry_repository_scope_invalid",
        "registry_repository_segment_invalid",
        "registry_required_host_missing",
        "registry_robot_duration_invalid",
        "registry_robot_name_invalid",
        "reimport_name_mismatch",
        "runtime_commit_asset_forbidden",
        "share_access_forbidden",
        "share_mode_invalid",
        "share_target_invalid",
        "tar_import_asset_archive_invalid",
        "tar_import_asset_required",
        "tar_import_parsed_image_missing",
        "tar_import_runtime_source_ref_required",
      ],
    },
  ];
